import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Paper,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Alert,
    TextField,
    Grid,
    TablePagination,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TableSortLabel,
    FormControlLabel,
    Checkbox,
    IconButton,
    InputAdornment,
    Chip,
} from '@mui/material';
import { Groups as GroupsIcon, QrCodeScanner as QrCodeScannerIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { memberService } from '../services/memberService';
import QRCodeScanner from '../components/QRCodeScanner';
import { useFeatureConfig } from '../hooks/useFeatureConfig';

const MemberList = () => {
    const navigate = useNavigate();
    const { qrCodeSupported, loaded } = useFeatureConfig();
    const [members, setMembers] = useState([]);
    const [initialLoading, setInitialLoading] = useState(true);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(50);
    const [totalCount, setTotalCount] = useState(0);
    const isFirstLoad = useRef(true);
    
    // Sorting states
    const [orderBy, setOrderBy] = useState('MembershipNumber');
    const [order, setOrder] = useState('asc');
    
    // Filter states
    const [filters, setFilters] = useState({
        membershipNumber: '',
        firstName: '',
        lastName: '',
        phone: '',
    });
    const [activeOnly, setActiveOnly] = useState(true); // Default to active members only
    const [qrScannerOpen, setQrScannerOpen] = useState(false);

    // Refs for input fields
    const membershipNumberRef = useRef(null);
    const firstNameRef = useRef(null);
    const lastNameRef = useRef(null);
    const phoneRef = useRef(null);

    // Debounced filters for API calls
    const [debouncedFilters, setDebouncedFilters] = useState(filters);

    // Debounce filter changes
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedFilters(filters);
        }, 500); // 500ms delay

        return () => clearTimeout(timer);
    }, [filters]);

    useEffect(() => {
        fetchMembers();
    }, [page, rowsPerPage, debouncedFilters, orderBy, order, activeOnly]);

    const fetchMembers = async () => {
        try {
            // Only show loading spinner on first load
            if (isFirstLoad.current) {
                setInitialLoading(true);
            }
            
            setError(null);
            
            const params = {
                page: page + 1, // Backend uses 1-based pagination
                pageSize: rowsPerPage,
                sortBy: orderBy,
                sortOrder: order.toUpperCase(),
                activeOnly: activeOnly,
            };
            
            // Add filters if they have values
            if (debouncedFilters.membershipNumber) params.membershipNumber = debouncedFilters.membershipNumber;
            if (debouncedFilters.firstName) params.firstName = debouncedFilters.firstName;
            if (debouncedFilters.lastName) params.lastName = debouncedFilters.lastName;
            if (debouncedFilters.phone) params.phone = debouncedFilters.phone;
            
            const data = await memberService.getMembers(params);
            setMembers(data.members || []);
            setTotalCount(data.pagination?.totalCount || 0);
            
            if (isFirstLoad.current) {
                isFirstLoad.current = false;
                setInitialLoading(false);
            }
        } catch (err) {
            console.error('Error fetching members:', err);
            setError('Failed to load members. Please try again.');
            if (isFirstLoad.current) {
                setInitialLoading(false);
                isFirstLoad.current = false;
            }
        }
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleFilterChange = (field) => (event) => {
        setFilters(prev => ({
            ...prev,
            [field]: event.target.value
        }));
        setPage(0); // Reset to first page when filtering
    };

    const handleSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleOpenQrScanner = () => {
        setQrScannerOpen(true);
    };

    const handleCloseQrScanner = () => {
        setQrScannerOpen(false);
    };

    const handleQrScanSuccess = (qrData) => {
        setFilters(prev => ({
            ...prev,
            membershipNumber: qrData
        }));
        setPage(0); // Reset to first page when filtering
    };

    const calculateAge = (dateOfBirth) => {
        if (!dateOfBirth) return 'N/A';
        
        const today = new Date();
        const birthDate = new Date(dateOfBirth);
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
            age--;
        }
        
        return age;
    };

    const formatPhoneNumber = (phone) => {
        if (!phone) return 'N/A';
        
        // Remove all non-digit characters
        const cleaned = phone.replace(/\D/g, '');
        
        // Check if it's a 10-digit number
        if (cleaned.length === 10) {
            return `(${cleaned.substring(0, 3)}) ${cleaned.substring(3, 6)}-${cleaned.substring(6)}`;
        }
        
        // Return original phone if not 10 digits
        return phone;
    };

    const handleRowClick = (member) => {
        navigate('/member-detail', {
            state: {
                memberId: member.ID,
                membershipNumber: member.MembershipNumber,
                phone: member.Phone
            }
        });
    };

    if (initialLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error && members.length === 0) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">{error}</Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <GroupsIcon color="primary" />
                Member List
            </Typography>

            {/* Filter Section */}
            <Paper sx={{ p: 2, mt: 3, mb: 2 }}>
                <Typography variant="h6" gutterBottom>
                    Filters
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            fullWidth
                            label="Membership #"
                            value={filters.membershipNumber}
                            onChange={handleFilterChange('membershipNumber')}
                            size="small"
                            inputRef={membershipNumberRef}
                            InputProps={{
                                endAdornment: (loaded && qrCodeSupported) ? (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={handleOpenQrScanner}
                                            edge="end"
                                            color="primary"
                                            aria-label="scan QR code"
                                            size="small"
                                        >
                                            <QrCodeScannerIcon />
                                        </IconButton>
                                    </InputAdornment>
                                ) : null,
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            fullWidth
                            label="First Name"
                            value={filters.firstName}
                            onChange={handleFilterChange('firstName')}
                            size="small"
                            inputRef={firstNameRef}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            fullWidth
                            label="Last Name"
                            value={filters.lastName}
                            onChange={handleFilterChange('lastName')}
                            size="small"
                            inputRef={lastNameRef}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            fullWidth
                            label="Phone"
                            value={filters.phone}
                            onChange={handleFilterChange('phone')}
                            size="small"
                            inputRef={phoneRef}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={activeOnly}
                                    onChange={(e) => setActiveOnly(e.target.checked)}
                                    color="primary"
                                />
                            }
                            label="Active Members Only"
                        />
                    </Grid>
                </Grid>
            </Paper>

            <Paper sx={{ width: '100%', overflow: 'hidden' }}>
                <TableContainer sx={{ maxHeight: 'calc(100vh - 450px)' }}>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell>
                                    <TableSortLabel
                                        active={orderBy === 'MembershipNumber'}
                                        direction={orderBy === 'MembershipNumber' ? order : 'asc'}
                                        onClick={() => handleSort('MembershipNumber')}
                                    >
                                        Membership #
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>
                                    <TableSortLabel
                                        active={orderBy === 'FirstName'}
                                        direction={orderBy === 'FirstName' ? order : 'asc'}
                                        onClick={() => handleSort('FirstName')}
                                    >
                                        First Name
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>
                                    <TableSortLabel
                                        active={orderBy === 'LastName'}
                                        direction={orderBy === 'LastName' ? order : 'asc'}
                                        onClick={() => handleSort('LastName')}
                                    >
                                        Last Name
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>
                                    <TableSortLabel
                                        active={orderBy === 'Phone'}
                                        direction={orderBy === 'Phone' ? order : 'asc'}
                                        onClick={() => handleSort('Phone')}
                                    >
                                        Phone
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>
                                    <TableSortLabel
                                        active={orderBy === 'DateOfBirth'}
                                        direction={orderBy === 'DateOfBirth' ? order : 'asc'}
                                        onClick={() => handleSort('DateOfBirth')}
                                    >
                                        Age
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>
                                    Current Tier
                                </TableCell>
                                <TableCell>
                                    Points for Next Tier
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {members.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center">
                                        <Typography variant="body2" color="text.secondary">
                                            No members found
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                members.map((member, index) => (
                                    <TableRow 
                                        key={index}
                                        hover
                                        onClick={() => handleRowClick(member)}
                                        sx={{ 
                                            '&:last-child td, &:last-child th': { border: 0 },
                                            cursor: 'pointer',
                                            '&:hover': {
                                                backgroundColor: 'action.hover'
                                            }
                                        }}
                                    >
                                        <TableCell>{member.MembershipNumber || 'N/A'}</TableCell>
                                        <TableCell>{member.FirstName}</TableCell>
                                        <TableCell>{member.LastName}</TableCell>
                                        <TableCell>{formatPhoneNumber(member.Phone)}</TableCell>
                                        <TableCell>{calculateAge(member.DateOfBirth)}</TableCell>
                                        <TableCell>
                                            <Chip 
                                                label={member.CurrentTier || 'No Tier'} 
                                                color="primary" 
                                                variant="outlined" 
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            {member.PointsNeededForNextTier > 0 ? 
                                                member.PointsNeededForNextTier.toLocaleString() : 
                                                'Max Tier'
                                            }
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                
                {/* Pagination */}
                <TablePagination
                    rowsPerPageOptions={[25, 50, 100]}
                    component="div"
                    count={totalCount}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Paper>

            <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                    Total Members: {totalCount}
                </Typography>
            </Box>

            {/* QR Code Scanner Dialog */}
            {loaded && qrCodeSupported && (
                <QRCodeScanner
                    open={qrScannerOpen}
                    onClose={handleCloseQrScanner}
                    onScanSuccess={handleQrScanSuccess}
                />
            )}
        </Box>
    );
};

export default MemberList;

